// Allow Hot Module Replacement (HMR) for JS files
// For more details see https://vitejs.dev/guide/api-hmr.html
if (import.meta.hot) {
  import.meta.hot.accept();
}

import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide.min.css';
import './quote-carousel.scss';

/**
 * QuoteCarousel Component
 * Initializes Splide carousel with a minimal JS-only configuration
 */
class QuoteCarousel {
  constructor(element) {
    this.element = element;
    this.carousel = null;
    this.init();
  }

  init() {
    const splideElement = this.element.querySelector('.quote-carousel__slides');
    const slideCount = splideElement?.querySelectorAll('.splide__slide').length ?? 0;

    if (!splideElement || this.element.dataset.initialized === 'true') {
      return;
    }

    if (slideCount <= 3) {
      this.element.dataset.hasCarousel = 'false';
      return;
    }

    const options = {
      type: 'slide',
      perPage: 1,
      gap: '24px',
      padding: {
        right: 'calc(var(--padding-m) + 2.5rem)',
      },
      mediaQuery: 'min',
      drag: true,
      keyboard: true,
      rewind: false,
      arrows: false,
      pagination: false,
      speed: 400,
      easing: 'ease-in-out',
      snap: true,
      breakpoints: {
        960: {
          perPage: 2,
          padding: {
            right: 'calc(var(--padding-m) + 2.5rem)',
          },
        },
        1200: {
          perPage: 3,
          padding: {
            right: 'var(--padding-4xl)',
          },
        },
        1920: {
          perPage: 3,
          padding: {
            right: 'calc((100vw - 99.5rem) / 2)',
          },
        },
      },
    };

    try {
      this.carousel = new Splide(splideElement, options);
      this.carousel.on('mounted refresh updated', () => this.removeRedundantAria(splideElement));
      this.carousel.mount();
      this.element.dataset.hasCarousel = 'true';
      this.element.dataset.initialized = 'true';
      this.setupArrowButtons();
    } catch (error) {
      this.element.dataset.hasCarousel = 'false';
      delete this.element.dataset.initialized;
      console.error('QuoteCarousel: Failed to initialize Splide', error);
    }
  }

  removeRedundantAria(splideElement) {
    splideElement.removeAttribute('role');
    splideElement.removeAttribute('aria-roledescription');

    splideElement.querySelectorAll('.splide__slide').forEach((slide) => {
      slide.removeAttribute('role');
      slide.removeAttribute('aria-roledescription');
    });
  }

  setupArrowButtons() {
    const prevButton = this.element.querySelector('.quote-carousel__arrow--prev');
    const nextButton = this.element.querySelector('.quote-carousel__arrow--next');

    const updateArrowState = () => {
      if (!this.carousel) {
        return;
      }

      const endIndex = this.carousel.Components.Controller.getEnd();
      prevButton?.toggleAttribute('disabled', this.carousel.index <= 0);
      nextButton?.toggleAttribute('disabled', this.carousel.index >= endIndex);
    };

    prevButton?.addEventListener('click', () => {
      const currentIndex = this.carousel?.index;
      if (typeof currentIndex !== 'number') {
        return;
      }

      this.carousel.go(currentIndex - 1);
    });

    nextButton?.addEventListener('click', () => {
      const currentIndex = this.carousel?.index;
      if (typeof currentIndex !== 'number') {
        return;
      }

      this.carousel.go(currentIndex + 1);
    });

    this.carousel?.on('mounted moved updated refresh resized', updateArrowState);
    updateArrowState();
  }
}

/**
 * Auto-initialize all QuoteCarousel instances on page
 */
document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('.quote-carousel');
  carousels.forEach((carousel) => {
    new QuoteCarousel(carousel);
  });
});
